import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import ServiceImageField, { ImageLoadState } from '../components/services/ServiceImageField';
import { normalizeImageUrl, isStoredServiceImage } from '../utils/imageUrl';
import {
  Add,
  Edit,
  Delete,
  LocalShipping,
  ElectricalServices,
  Plumbing,
  AcUnit,
  Build,
  Payment,
  Category,
  ExpandMore,
  ChevronRight,
  Warning,
  Sort,
  ArrowUpward,
  ArrowDownward,
  FilterList,
  Restaurant,
  LocalHospital,
  School,
  ShoppingCart,
  DirectionsCar,
  DirectionsBike,
  HomeRepairService,
  CleaningServices,
  Pets,
  ChildCare,
  SportsSoccer,
  Computer,
  Phone,
  LocalLibrary,
  LocalLaundryService,
  BusinessCenter,
  FitnessCenter,
  MedicalServices,
  Agriculture,
  Construction,
  RoomService,
  LocalFlorist,
  Event,
  PhotoCamera,
  MusicNote,
  Brush,
  Kitchen,
  LocalGroceryStore,
  LocalPharmacy,
  AccountBalance,
  LocalAtm,
  CreditCard,
  Timeline,
  Save,
  Close,
  Done,
  Cancel,
  Refresh,
  Settings,
  Share,
  Star,
  Email,
  Message,
  Chat,
  VideoCall,
  LocalCafe,
  LocalPizza,
  Cake,
  Home,
  House,
  LocalTaxi,
  Train,
  Flight,
  TwoWheeler,
  Smartphone,
  Work,
  Business,
  Person,
  Group,
  LocationOn,
  AccessTime,
  LocalFireDepartment,
  ReportProblem,
  Announcement,
  PriorityHigh,
  NewReleases,
  NotificationImportant,
  Error,
  Spa,
  ContentCut,
  SelfImprovement,
  FaceRetouchingNatural,
} from '@mui/icons-material';
import adminService from '../services/adminService';
import { addSampleServiceRates } from '../utils/addSampleRates';
import IconPicker from '../components/IconPicker';
import { ServiceSegment, ServiceCategory, ServiceRate } from '../types/services';
import { 
  convertToMinutes, 
  convertFromMinutes, 
  validateDuration, 
  formatDurationForAPI 
} from '../utils/durationUtils';
import { EmptyState, PageHeader, ResponsiveTable, StatusChip } from '../components/common';

interface PaymentGateway {
  id: string;
  name: string;
  isEnabled: boolean;
  processingFee: number;
  config: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const ServicesPricing: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  // Delete confirmation states
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    type: 'segment' | 'category' | 'rate';
    id: string;
    name: string;
  }>({ open: false, type: 'segment', id: '', name: '' });

  // Loading states
  const [loading, setLoading] = useState({
    segment: false,
    category: false,
    rate: false,
    delete: false
  });

  // Service Segments State - will be loaded from Firebase
  const [segments, setSegments] = useState<ServiceSegment[]>([]);
  const [segmentDialogOpen, setSegmentDialogOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<ServiceSegment | null>(null);

  // Service Categories State - will be loaded from Firebase
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);

  // Duplicate Detection State
  const [segmentDuplicates, setSegmentDuplicates] = useState<{ duplicates: { name: string; count: number; ids: string[] }[]; totalDuplicates: number }>({ duplicates: [], totalDuplicates: 0 });
  const [categoryDuplicates, setCategoryDuplicates] = useState<{ duplicates: { name: string; segmentId: string; count: number; ids: string[] }[]; totalDuplicates: number }>({ duplicates: [], totalDuplicates: 0 });

  // Service Rates State - will be loaded from Firebase
  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<ServiceRate | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set()); // Will be populated dynamically
  
  // Duration state for the form (hours and minutes)
  const [durationHours, setDurationHours] = useState<number>(0);
  const [durationMinutes, setDurationMinutes] = useState<number>(0);
  const [rateImageState, setRateImageState] = useState<ImageLoadState>('empty');
  
  // Sorting state for different sections
  const [sortConfig, setSortConfig] = useState({
    segments: { field: 'order', direction: 'asc' as 'asc' | 'desc' },
    categories: { field: 'name', direction: 'asc' as 'asc' | 'desc' },
    serviceRates: { field: 'basePrice', direction: 'asc' as 'asc' | 'desc' }
  });
  
  // Helper function to handle number input validation
  const handleNumberInput = (value: string, min: number = 0, max?: number): number => {
    // Remove any non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Handle empty input
    if (!cleanValue) return min;
    
    // Parse the number
    const numValue = parseFloat(cleanValue);
    
    // Check if it's a valid number
    if (isNaN(numValue)) return min;
    
    // Apply min/max constraints
    if (numValue < min) return min;
    if (max !== undefined && numValue > max) return max;
    
    return numValue;
  };

  // Helper function to validate number input on keypress
  const handleNumberKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  // Sorting utility functions
  const handleSort = (section: 'segments' | 'categories' | 'serviceRates', field: string) => {
    setSortConfig(prev => ({
      ...prev,
      [section]: {
        field,
        direction: prev[section].field === field && prev[section].direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const sortData = <T extends any>(data: T[], sortField: string, direction: 'asc' | 'desc'): T[] => {
    return [...data].sort((a, b) => {
      const aVal = getNestedValue(a, sortField);
      const bVal = getNestedValue(b, sortField);
      
      // Handle different data types
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      if (aVal instanceof Date && bVal instanceof Date) {
        return direction === 'asc' ? aVal.getTime() - bVal.getTime() : bVal.getTime() - aVal.getTime();
      }
      
      // Fallback to string comparison
      return direction === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const getSortIcon = (section: 'segments' | 'categories' | 'serviceRates', field: string) => {
    const config = sortConfig[section];
    if (config.field !== field) return null;
    return config.direction === 'asc' ? '↑' : '↓';
  };

  // Special sorting logic for delivery services (weight-based)
  const getIntelligentSortField = (categoryName: string): string => {
    const lowerCaseName = categoryName.toLowerCase();
    if (lowerCaseName.includes('delivery') || lowerCaseName.includes('package') || lowerCaseName.includes('courier')) {
      return 'maxWeight'; // Sort delivery services by weight (kg)
    }
    return 'basePrice'; // Default to price sorting for other services
  };

  const getSortDisplayName = (field: string): string => {
    switch (field) {
      case 'serviceName': return 'Name';
      case 'basePrice': return 'Base Price';
      case 'pricePerUnit': return 'Price per Unit';
      case 'maxWeight': return 'Max Weight (Kg)';
      case 'durationInMinutes': return 'Duration';
      case 'urgencyMultipliers.express': return 'Express Rate';
      case 'urgencyMultipliers.urgent': return 'Urgent Rate';
      case 'createdAt': return 'Created Date';
      case 'isActive': return 'Status';
      default: return 'Custom';
    }
  };

  // Load data from Firebase
  React.useEffect(() => {
    const initializeData = async () => {
      try {
        // Load segments first (categories depend on segments)
        await loadSegments();
        
        // Then load categories (service rates depend on categories)
        await loadCategories();
        
        // Load service rates
        await loadServiceRates();
        
        // Finally load duplicate detection data
        await loadDuplicateDetection();
      } catch (error) {
        // Surfaced rather than logged only: on failure the page renders empty
        // lists, which looks identical to "nothing is configured yet" and has
        // led to duplicate services being created.
        console.error('Error initializing data:', error);
        setSnackbar({
          open: true,
          message: 'Could not load services and pricing. Some data may be missing — try refreshing.',
          severity: 'error',
        });
      }
    };

    initializeData();
  }, []);

  const loadSegments = async () => {
    try {
      const segmentsData = await adminService.getAllSegments();
      setSegments(segmentsData);
    } catch (error) {
      console.error('Error loading segments:', error);
      setSnackbar({ open: true, message: 'Could not load segments.', severity: 'error' });
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await adminService.getAllCategories();
      setCategories(categoriesData);
      // Auto-expand all categories
      setExpandedCategories(new Set(categoriesData.map(c => c.id)));
    } catch (error) {
      console.error('Error loading categories:', error);
      setSnackbar({ open: true, message: 'Could not load categories.', severity: 'error' });
    }
  };

  const loadServiceRates = async () => {
    try {
      const ratesData = await adminService.getAllServiceRates();
      setServiceRates(ratesData);
    } catch (error) {
      console.error('Error loading service rates:', error);
      setSnackbar({ open: true, message: 'Could not load service rates.', severity: 'error' });
    }
  };

  const loadDuplicateDetection = async () => {
    try {
      const [segmentDups, categoryDups] = await Promise.all([
        adminService.detectDuplicateSegments(),
        adminService.detectDuplicateCategories()
      ]);
      
      setSegmentDuplicates(segmentDups);
      setCategoryDuplicates(categoryDups);
      
      console.log('Duplicate detection loaded:', {
        segments: segmentDups.totalDuplicates,
        categories: categoryDups.totalDuplicates
      });
    } catch (error) {
      console.error('Error loading duplicate detection:', error);
    }
  };

  // Helper function to check if a segment is a duplicate
  const isSegmentDuplicate = (segmentName: string, segmentId: string): { isDuplicate: boolean; count: number } => {
    const duplicateGroup = segmentDuplicates.duplicates.find(dup => dup.name === segmentName);
    if (duplicateGroup && duplicateGroup.ids.includes(segmentId)) {
      return { isDuplicate: true, count: duplicateGroup.count };
    }
    return { isDuplicate: false, count: 0 };
  };

  // Helper function to check if a category is a duplicate
  const isCategoryDuplicate = (categoryName: string, categoryId: string, segmentId: string): { isDuplicate: boolean; count: number } => {
    const duplicateGroup = categoryDuplicates.duplicates.find(dup => 
      dup.name === categoryName && dup.segmentId === segmentId
    );
    if (duplicateGroup && duplicateGroup.ids.includes(categoryId)) {
      return { isDuplicate: true, count: duplicateGroup.count };
    }
    return { isDuplicate: false, count: 0 };
  };

  const cleanupDuplicateSegments = async () => {
    try {
      setSnackbar({ open: true, message: 'Scanning for duplicate segments...', severity: 'info' });
      
      const result = await adminService.cleanupDuplicateSegments();
      
      if (result.success) {
        if (result.duplicatesRemoved > 0) {
          console.log('Cleanup details:', result.details);
          setSnackbar({ 
            open: true, 
            message: `Successfully removed ${result.duplicatesRemoved} duplicate segments`, 
            severity: 'success' 
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: 'No duplicate segments found - database is clean!', 
            severity: 'info' 
          });
        }
        await loadSegments();
        await loadCategories();
        await loadServiceRates();
        await loadDuplicateDetection();
      } else {
        console.error('Cleanup failed:', result.details);
        setSnackbar({ 
          open: true, 
          message: `Cleanup failed: ${result.details[0] || 'Unknown error'}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Error cleaning up duplicate segments:', error);
      setSnackbar({ open: true, message: 'Error cleaning up duplicate segments', severity: 'error' });
    }
  };

  const cleanupDuplicateCategories = async () => {
    try {
      setSnackbar({ open: true, message: 'Scanning for duplicate categories...', severity: 'info' });
      
      const result = await adminService.cleanupDuplicateCategories();
      
      if (result.success) {
        if (result.duplicatesRemoved > 0) {
          console.log('Categories cleanup details:', result.details);
          setSnackbar({ 
            open: true, 
            message: `Successfully removed ${result.duplicatesRemoved} duplicate categories`, 
            severity: 'success' 
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: 'No duplicate categories found - database is clean!', 
            severity: 'info' 
          });
        }
        await loadCategories();
        await loadServiceRates();
        await loadDuplicateDetection();
      } else {
        console.error('Categories cleanup failed:', result.details);
        setSnackbar({ 
          open: true, 
          message: `Categories cleanup failed: ${result.details[0] || 'Unknown error'}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Error cleaning up duplicate categories:', error);
      setSnackbar({ open: true, message: 'Error cleaning up duplicate categories', severity: 'error' });
    }
  };

  const cleanupOrphanedData = async () => {
    try {
      setSnackbar({ open: true, message: 'Scanning for orphaned data...', severity: 'info' });
      
      const result = await adminService.cleanupAllOrphanedData();
      
      if (result.success) {
        const totalRemoved = result.categoriesRemoved + result.serviceRatesRemoved;
        if (totalRemoved > 0) {
          console.log('Orphaned data cleanup details:', result.details);
          setSnackbar({ 
            open: true, 
            message: `Successfully removed ${result.categoriesRemoved} orphaned categories and ${result.serviceRatesRemoved} orphaned service rates`, 
            severity: 'success' 
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: 'No orphaned data found - database is clean!', 
            severity: 'info' 
          });
        }
        await loadCategories();
        await loadServiceRates();
        await loadDuplicateDetection();
      } else {
        console.error('Orphaned data cleanup failed:', result.details);
        setSnackbar({ 
          open: true, 
          message: `Orphaned data cleanup failed: ${result.details[0] || 'Unknown error'}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Error cleaning up orphaned data:', error);
      setSnackbar({ open: true, message: 'Error cleaning up orphaned data', severity: 'error' });
    }
  };

  const handleAddSampleServiceRates = async () => {
    try {
      setSnackbar({ open: true, message: 'Adding sample service rates...', severity: 'info' });
      
      const result = await addSampleServiceRates();
      
      if (result && result.totalAdded > 0) {
        setSnackbar({ 
          open: true, 
          message: `Successfully added ${result.totalAdded} service rates${result.totalSkipped > 0 ? ` (skipped ${result.totalSkipped} due to missing categories)` : ''}`, 
          severity: 'success' 
        });
        await loadServiceRates();
        await loadCategories();
      } else {
        setSnackbar({ 
          open: true, 
          message: 'No service rates were added. Please check if categories exist.', 
          severity: 'info' 
        });
      }
    } catch (error) {
      console.error('Error adding sample service rates:', error);
      setSnackbar({ open: true, message: 'Error adding sample service rates', severity: 'error' });
    }
  };

  const handleResetDatabase = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will completely clear all segments, categories, and service rates from the database.\n\n' +
      'This action cannot be undone!\n\n' +
      'You will need to manually add all data back.\n\n' +
      'Are you sure you want to proceed?'
    );
    
    if (!confirmed) return;
    
    const doubleConfirmed = window.confirm(
      '🚨 FINAL CONFIRMATION\n\n' +
      'This will DELETE ALL existing data. The database will be completely empty.\n\n' +
      'Click OK to proceed or Cancel to abort.'
    );
    
    if (!doubleConfirmed) return;
    
    try {
      setSnackbar({ open: true, message: '🔄 Resetting database... This may take a few moments', severity: 'info' });
      
      const result = await adminService.resetDatabase();
      
      if (result.success) {
        console.log('Database reset details:', result.details);
        setSnackbar({ 
          open: true, 
          message: '🎉 Database reset successfully! All data has been cleared.', 
          severity: 'success' 
        });
        
        // Reload all data
        await loadSegments();
        await loadCategories();
        await loadServiceRates();
        await loadDuplicateDetection();
      } else {
        console.error('Database reset failed:', result.details);
        setSnackbar({ 
          open: true, 
          message: `❌ Database reset failed: ${result.details.join(', ')}`, 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Error during database reset:', error);
      setSnackbar({ open: true, message: '❌ Error during database reset', severity: 'error' });
    }
  };

  // Payment Gateways State - can be made dynamic later if needed
  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway[]>([
    { id: '1', name: 'Razorpay', isEnabled: true, processingFee: 2.5, config: {} },
    { id: '2', name: 'Stripe', isEnabled: false, processingFee: 2.9, config: {} },
    { id: '3', name: 'PayPal', isEnabled: false, processingFee: 3.4, config: {} },
  ]);
  const [paymentSettings, setPaymentSettings] = useState({
    codEnabled: true,
    minimumOrderAmount: 100,
    maxCodAmount: 5000,
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      // Action Icons
      Add: <Add />,
      Edit: <Edit />,
      Delete: <Delete />,
      Save: <Save />,
      Close: <Close />,
      Done: <Done />,
      Cancel: <Cancel />,
      Refresh: <Refresh />,
      Settings: <Settings />,
      Share: <Share />,
      Star: <Star />,
      
      // Communication Icons
      Phone: <Phone />,
      Email: <Email />,
      Message: <Message />,
      Chat: <Chat />,
      VideoCall: <VideoCall />,
      
      // Food & Drink Icons
      Restaurant: <Restaurant />,
      LocalCafe: <LocalCafe />,
      LocalPizza: <LocalPizza />,
      Cake: <Cake />,
      LocalGroceryStore: <LocalGroceryStore />,
      Kitchen: <Kitchen />,
      
      // Healthcare Icons
      LocalHospital: <LocalHospital />,
      MedicalServices: <MedicalServices />,
      LocalPharmacy: <LocalPharmacy />,
      
      // Emergency Icons
      LocalFireDepartment: <LocalFireDepartment />,
      Warning: <Warning />,
      ReportProblem: <ReportProblem />,
      Error: <Error />,
      Announcement: <Announcement />,
      PriorityHigh: <PriorityHigh />,
      NewReleases: <NewReleases />,
      NotificationImportant: <NotificationImportant />,
      
      // Home & Living Icons
      Home: <Home />,
      House: <House />,
      
      // Transportation & Delivery Icons
      LocalShipping: <LocalShipping />,
      DirectionsCar: <DirectionsCar />,
      DirectionsBike: <DirectionsBike />,
      LocalTaxi: <LocalTaxi />,
      Train: <Train />,
      Flight: <Flight />,
      TwoWheeler: <TwoWheeler />,
      
      // Services Icons
      Build: <Build />,
      Construction: <Construction />,
      ElectricalServices: <ElectricalServices />,
      Plumbing: <Plumbing />,
      HomeRepairService: <HomeRepairService />,
      CleaningServices: <CleaningServices />,
      LocalLaundryService: <LocalLaundryService />,
      AcUnit: <AcUnit />,
      Spa: <Spa />,
      ContentCut: <ContentCut />,
      SelfImprovement: <SelfImprovement />,
      
      // Technology Icons
      Computer: <Computer />,
      Smartphone: <Smartphone />,
      PhotoCamera: <PhotoCamera />,
      
      // Business Icons
      Work: <Work />,
      Business: <Business />,
      Payment: <Payment />,
      CreditCard: <CreditCard />,
      ShoppingCart: <ShoppingCart />,
      LocalAtm: <LocalAtm />,
      AccountBalance: <AccountBalance />,
      Timeline: <Timeline />,
      BusinessCenter: <BusinessCenter />,
      
      // People Icons
      Person: <Person />,
      Group: <Group />,
      ChildCare: <ChildCare />,
      
      // Places & Activities Icons
      School: <School />,
      LocalLibrary: <LocalLibrary />,
      FitnessCenter: <FitnessCenter />,
      SportsSoccer: <SportsSoccer />,
      Pets: <Pets />,
      Agriculture: <Agriculture />,
      LocalFlorist: <LocalFlorist />,
      
      // General Icons
      Category: <Category />,
      LocationOn: <LocationOn />,
      AccessTime: <AccessTime />,
      Event: <Event />,
      RoomService: <RoomService />,
      MusicNote: <MusicNote />,
      Brush: <Brush />,
      Beauty: <FaceRetouchingNatural />,
    };
    return icons[iconName] || <Category />;
  };

  // Segment Management
  const handleAddSegment = () => {
    setEditingSegment({
      id: '',
      name: '',
      icon: 'Build',
      description: '',
      color: '#FF6B35',
      isActive: true,
      order: segments.length + 1,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    setSegmentDialogOpen(true);
  };

  const handleEditSegment = (segment: ServiceSegment) => {
    setEditingSegment(segment);
    setSegmentDialogOpen(true);
  };

  const handleSaveSegment = async () => {
    if (!editingSegment) return;
    
    // Validation
    if (!editingSegment.name.trim()) {
      showSnackbar('Segment name is required', 'error');
      return;
    }
    if (!editingSegment.description.trim()) {
      showSnackbar('Description is required', 'error');
      return;
    }

    setLoading(prev => ({ ...prev, segment: true }));
    try {
      if (editingSegment.id) {
        // Update existing segment
        await adminService.updateSegment(editingSegment.id, editingSegment);
      } else {
        // Create new segment
        await adminService.createSegment({
          name: editingSegment.name,
          icon: editingSegment.icon,
          description: editingSegment.description,
          color: editingSegment.color,
          isActive: editingSegment.isActive,
          order: editingSegment.order,
        });
      }
      
      // Reload segments from Firebase
      await loadSegments();
      setSegmentDialogOpen(false);
      setEditingSegment(null);
      showSnackbar(editingSegment.id ? 'Segment updated successfully' : 'Segment created successfully', 'success');
    } catch (error) {
      console.error('Error saving segment:', error);
      showSnackbar('Error saving segment', 'error');
    } finally {
      setLoading(prev => ({ ...prev, segment: false }));
    }
  };

  const handleDeleteSegment = (segmentId: string) => {
    const segment = segments.find(s => s.id === segmentId);
    setDeleteConfirm({
      open: true,
      type: 'segment',
      id: segmentId,
      name: segment?.name || 'this segment'
    });
  };

  const confirmDeleteSegment = async () => {
    setLoading(prev => ({ ...prev, delete: true }));
    try {
      await adminService.deleteSegment(deleteConfirm.id);
      // Reload all data to reflect cascade deletions
      await Promise.all([
        loadSegments(),
        loadCategories(),
        loadServiceRates()
      ]);
      showSnackbar('Segment and all associated categories and rates deleted successfully', 'success');
      setDeleteConfirm({ open: false, type: 'segment', id: '', name: '' });
    } catch (error) {
      console.error('Error deleting segment:', error);
      showSnackbar('Error deleting segment', 'error');
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  // Category Management
  const handleAddCategory = () => {
    setEditingCategory({
      id: '',
      segmentId: segments[0]?.id || '',
      name: '',
      icon: 'Build',
      description: '',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    setCategoryDialogOpen(true);
  };

  const handleEditCategory = (category: ServiceCategory) => {
    setEditingCategory(category);
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!editingCategory) return;
    
    // Validation
    if (!editingCategory.name.trim()) {
      showSnackbar('Category name is required', 'error');
      return;
    }
    if (!editingCategory.description.trim()) {
      showSnackbar('Description is required', 'error');
      return;
    }
    if (!editingCategory.segmentId) {
      showSnackbar('Please select a segment', 'error');
      return;
    }

    setLoading(prev => ({ ...prev, category: true }));
    try {
      if (editingCategory.id) {
        // Update existing category
        await adminService.updateCategory(editingCategory.id, editingCategory);
      } else {
        // Create new category
        await adminService.createCategory({
          segmentId: editingCategory.segmentId,
          name: editingCategory.name,
          icon: editingCategory.icon,
          description: editingCategory.description,
          isActive: editingCategory.isActive,
        });
      }
      
      // Update local state
      if (editingCategory.id) {
        // Existing category - local optimistic update is fine
        const updatedCategories = categories.map(c => c.id === editingCategory.id ? editingCategory : c);
        setCategories(updatedCategories);
      } else {
        // Newly created category: reload from backend to get the real Firestore document ID
        // This avoids using a temporary numeric id (Date.now) which would later be written into related documents
        await loadCategories();
      }

      setCategoryDialogOpen(false);
      setEditingCategory(null);
      showSnackbar(editingCategory.id ? 'Category updated successfully' : 'Category created successfully', 'success');
    } catch (error) {
      console.error('Error saving category:', error);
      showSnackbar('Error saving category', 'error');
    } finally {
      setLoading(prev => ({ ...prev, category: false }));
    }
  };

  const handleDeleteCategory = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    setDeleteConfirm({
      open: true,
      type: 'category',
      id: categoryId,
      name: category?.name || 'this category'
    });
  };

  const confirmDeleteCategory = async () => {
    setLoading(prev => ({ ...prev, delete: true }));
    try {
      await adminService.deleteCategory(deleteConfirm.id);
      setCategories(categories.filter(c => c.id !== deleteConfirm.id));
      showSnackbar('Category deleted successfully', 'success');
      setDeleteConfirm({ open: false, type: 'category', id: '', name: '' });
    } catch (error) {
      console.error('Error deleting category:', error);
      showSnackbar('Error deleting category', 'error');
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  // Service Rate Management
  const handleAddRate = () => {
    setEditingRate({
      id: '',
      categoryId: categories[0]?.id || '',
      serviceName: '',
      basePrice: 0,
      pricePerUnit: 0,
      unit: 'hour',
      maxWeight: 0,
      taskType: undefined, // Initialize taskType field
      durationInMinutes: undefined,
      urgencyMultipliers: { standard: 1.0, express: 1.5, urgent: 2.0 },
      urgencyTimes: { standard: '', express: '', urgent: '' },
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    // Reset duration form state
    setDurationHours(0);
    setDurationMinutes(0);
    setRateDialogOpen(true);
  };

  const handleEditRate = (rate: ServiceRate) => {
    setEditingRate({
      ...rate,
      urgencyTimes: rate.urgencyTimes || { standard: '', express: '', urgent: '' }
    });
    
    // Set duration form state from existing rate
    const duration = convertFromMinutes(rate.durationInMinutes);
    setDurationHours(duration.hours);
    setDurationMinutes(duration.minutes);
    
    setRateDialogOpen(true);
  };

  const handleSaveRate = async () => {
    if (!editingRate) return;
    
    // Validation
    if (!editingRate.serviceName.trim()) {
      showSnackbar('Service name is required', 'error');
      return;
    }
    if (!editingRate.categoryId) {
      showSnackbar('Please select a category', 'error');
      return;
    }
    if (!editingRate.basePrice || editingRate.basePrice <= 0) {
      showSnackbar('Base price must be greater than 0', 'error');
      return;
    }
    // Issue #282: Make Price Per Unit mandatory
    if (!editingRate.pricePerUnit || editingRate.pricePerUnit <= 0) {
      showSnackbar('Price per unit is required and must be greater than 0', 'error');
      return;
    }

    // A pasted image must actually load before it's saved — otherwise the app
    // would show a broken tile to every customer.
    if (normalizeImageUrl(editingRate.imageUrl)) {
      if (rateImageState === 'loading') {
        showSnackbar('The image is still loading — wait a moment and save again', 'info');
        return;
      }
      if (rateImageState === 'error') {
        showSnackbar("The image link doesn't load. Fix it or clear the field.", 'error');
        return;
      }
    }

    // Validate and convert duration
    const durationValidation = validateDuration(durationHours, durationMinutes);
    if (!durationValidation.isValid) {
      showSnackbar(durationValidation.error || 'Invalid duration', 'error');
      return;
    }

    // Calculate total duration in minutes (0 means no duration specified)
    const totalDurationInMinutes = (durationHours === 0 && durationMinutes === 0) 
      ? undefined 
      : convertToMinutes(durationHours, durationMinutes);

    setLoading(prev => ({ ...prev, rate: true }));
    try {
      // Validate urgency time duration formats if provided
      const durationPattern = /^\s*(\d+(?:\.\d+)?)\s*(mins?|minutes?|hrs?|hours?)\s*$/i;
      const checkDuration = (s?: string) => !s || durationPattern.test(s);
      if (!checkDuration(editingRate.urgencyTimes?.standard)) {
        showSnackbar('Standard Time format is invalid. Use values like "30 mins", "2 hrs", or "1.5 hrs".', 'error');
        setLoading(prev => ({ ...prev, rate: false }));
        return;
      }
      if (!checkDuration(editingRate.urgencyTimes?.express)) {
        showSnackbar('Express Time format is invalid. Use values like "30 mins", "2 hrs", or "1.5 hrs".', 'error');
        setLoading(prev => ({ ...prev, rate: false }));
        return;
      }
      if (!checkDuration(editingRate.urgencyTimes?.urgent)) {
        showSnackbar('Urgent Time format is invalid. Use values like "30 mins", "2 hrs", or "1.5 hrs".', 'error');
        setLoading(prev => ({ ...prev, rate: false }));
        return;
      }

      // Copy a newly pasted link into our own Storage before saving, so the
      // service never depends on a third-party host keeping the file up.
      // A link that already points at our stored copy is saved as it is.
      let imageUrl = normalizeImageUrl(editingRate.imageUrl);
      let imageSourceUrl = editingRate.imageSourceUrl;
      if (imageUrl && !isStoredServiceImage(imageUrl)) {
        showSnackbar('Copying the image into PickupNinja storage…', 'info');
        try {
          const stored = await adminService.importServiceImage(imageUrl);
          imageUrl = stored.url;
          imageSourceUrl = stored.sourceUrl;
        } catch (err: any) {
          showSnackbar(err?.message || "Couldn't copy the image into storage", 'error');
          return;
        }
      }
      if (!imageUrl) imageSourceUrl = undefined;

      // Update editingRate with calculated duration
      const updatedRate = {
        ...editingRate,
        durationInMinutes: totalDurationInMinutes,
        imageUrl,
        imageSourceUrl,
      };

      // Both calls report failure by returning false rather than throwing;
      // check it, or a failed save would be announced as a success.
      let saved: boolean;
      if (editingRate.id) {
        // Update existing rate
        saved = await adminService.updateServiceRate(editingRate.id, updatedRate);
      } else {
        // Create new rate
        saved = await adminService.createServiceRate({
          categoryId: updatedRate.categoryId,
          serviceName: updatedRate.serviceName,
          basePrice: updatedRate.basePrice,
          pricePerUnit: updatedRate.pricePerUnit,
          unit: updatedRate.unit,
          maxWeight: updatedRate.maxWeight,
          durationInMinutes: updatedRate.durationInMinutes,
          taskType: updatedRate.taskType, // Include taskType
          imageUrl: updatedRate.imageUrl,
          imageSourceUrl: updatedRate.imageSourceUrl,
          subscriptionEnabled: updatedRate.subscriptionEnabled,
          subscriptionPrice: updatedRate.subscriptionPrice,
          urgencyMultipliers: updatedRate.urgencyMultipliers,
          urgencyTimes: updatedRate.urgencyTimes,
          isActive: updatedRate.isActive,
        });
      }
      if (!saved) {
        showSnackbar('Error saving service rate', 'error');
        return;
      }
      
      // Update local state
      if (editingRate.id) {
        const updatedRates = serviceRates.map(r =>
          r.id === editingRate.id ? { ...updatedRate, imageUrl: imageUrl || undefined } : r
        );
        setServiceRates(updatedRates);
      } else {
        // Reload from backend to get real Firestore IDs for newly created rates
        await loadServiceRates();
      }
      setRateDialogOpen(false);
      setEditingRate(null);
      showSnackbar(editingRate.id ? 'Service rate updated successfully' : 'Service rate created successfully', 'success');
    } catch (error) {
      console.error('Error saving service rate:', error);
      showSnackbar('Error saving service rate', 'error');
    } finally {
      setLoading(prev => ({ ...prev, rate: false }));
    }
  };

  const handleDeleteRate = (rateId: string) => {
    const rate = serviceRates.find(r => r.id === rateId);
    setDeleteConfirm({
      open: true,
      type: 'rate',
      id: rateId,
      name: rate?.serviceName || 'this service rate'
    });
  };

  const confirmDeleteRate = async () => {
    setLoading(prev => ({ ...prev, delete: true }));
    try {
      await adminService.deleteServiceRate(deleteConfirm.id);
      setServiceRates(serviceRates.filter(r => r.id !== deleteConfirm.id));
      showSnackbar('Service rate deleted successfully', 'success');
      setDeleteConfirm({ open: false, type: 'rate', id: '', name: '' });
    } catch (error) {
      console.error('Error deleting service rate:', error);
      showSnackbar('Error deleting service rate', 'error');
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  // Payment Gateway Management
  const handleToggleGateway = (gatewayId: string, enabled: boolean) => {
    setPaymentGateways(gateways => 
      gateways.map(g => g.id === gatewayId ? { ...g, isEnabled: enabled } : g)
    );
    showSnackbar('Payment gateway updated', 'success');
  };


  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderTreeViewServiceRates = () => {
    const segmentsWithCategoriesAndServices = segments.map(segment => ({
      ...segment,
      categories: categories.filter(category => category.segmentId === segment.id).map(category => {
        // Use intelligent sorting for each category
        const intelligentSortField = getIntelligentSortField(category.name);
        const sortField = sortConfig.serviceRates.field === 'basePrice' ? intelligentSortField : sortConfig.serviceRates.field;
        
        return {
          ...category,
          services: sortData(
            serviceRates.filter(rate => rate.categoryId === category.id),
            sortField,
            sortConfig.serviceRates.direction
          ),
          recommendedSortField: intelligentSortField
        };
      })
    }));

    if (segmentsWithCategoriesAndServices.length === 0) {
      return (
        <Paper sx={{ p: 2 }}>
          <EmptyState
            title="No service rates yet"
            description="Rates are set per category. Add a service segment and category first, then come back here to price them."
          />
        </Paper>
      );
    }

    return (
      <Paper sx={{ p: 2 }}>
        {segmentsWithCategoriesAndServices.map((segment) => (
          <Box key={segment.id} sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                bgcolor: segment.color + '20',
                borderRadius: 1,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: segment.color + '40',
                '&:hover': {
                  bgcolor: segment.color + '30',
                },
              }}
              onClick={() => toggleCategoryExpansion(segment.id)}
            >
              <IconButton size="small" sx={{ mr: 1 }}>
                {expandedCategories.has(segment.id) ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
              {getIconComponent(segment.icon)}
              <Typography variant="h5" sx={{ ml: 2, flexGrow: 1, fontWeight: 600 }}>
                {segment.name}
              </Typography>
              <Chip
                label={`${segment.categories.length} categories`}
                size="small"
                sx={{ mr: 1, bgcolor: segment.color + '30' }}
              />
              <Chip
                label={segment.isActive ? 'Active' : 'Inactive'}
                color={segment.isActive ? 'success' : 'default'}
                size="small"
              />
            </Box>
            
            {expandedCategories.has(segment.id) && (
              <Box sx={{ ml: 2, mt: 2 }}>
                {segment.categories.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No categories configured for this segment
                  </Typography>
                ) : (
                  segment.categories.map((category) => (
                    <Box key={category.id} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 2,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                        onClick={() => toggleCategoryExpansion(category.id)}
                      >
                        <IconButton size="small" sx={{ mr: 1 }}>
                          {expandedCategories.has(category.id) ? <ExpandMore /> : <ChevronRight />}
                        </IconButton>
                        {getIconComponent(category.icon)}
                        <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
                          {category.name}
                        </Typography>
                        <Chip
                          label={`${category.services.length} services`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        {/* Quick sort button for delivery categories */}
                        {category.recommendedSortField === 'maxWeight' && sortConfig.serviceRates.field !== 'maxWeight' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSort('serviceRates', 'maxWeight');
                            }}
                            sx={{ mr: 1, fontSize: '0.7rem', py: 0.5 }}
                          >
                            Sort by Weight
                          </Button>
                        )}
                        <Chip
                          label={category.isActive ? 'Active' : 'Inactive'}
                          color={category.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      
                      {expandedCategories.has(category.id) && (
                        <Box sx={{ ml: 4, mt: 2 }}>
                          {category.services.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                              No services configured for this category
                            </Typography>
                          ) : (
                            <>
                              {/* Show current sorting info */}
                              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Sort sx={{ fontSize: 16 }} />
                                <Typography variant="caption" color="text.secondary">
                                  Sorted by: {getSortDisplayName(
                                    sortConfig.serviceRates.field === 'basePrice' ? 
                                    category.recommendedSortField : 
                                    sortConfig.serviceRates.field
                                  )} 
                                  ({sortConfig.serviceRates.direction === 'asc' ? 'Low to High' : 'High to Low'})
                                </Typography>
                                {category.recommendedSortField === 'maxWeight' && sortConfig.serviceRates.field === 'basePrice' && (
                                  <Chip 
                                    label="Smart: Weight-based sorting for delivery" 
                                    size="small" 
                                    variant="outlined" 
                                    color="info"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                              <List>
                                {category.services.map((service) => (
                                <ListItem
                                  key={service.id}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                    bgcolor: 'background.paper',
                                    px: 4, // Issue #299: Add proper horizontal padding for better spacing
                                    py: 2, // Issue #299: Add vertical padding for balanced layout
                                  }}
                                >
                                  {service.imageUrl && (
                                    <Box
                                      component="img"
                                      src={service.imageUrl}
                                      alt=""
                                      sx={{ width: 56, height: 56, borderRadius: 2, objectFit: 'cover', mr: 2, flexShrink: 0, bgcolor: 'grey.100' }}
                                    />
                                  )}
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                          {service.serviceName}
                                        </Typography>
                                        {service.maxWeight && (
                                          <Chip
                                            label={`${service.maxWeight}kg`}
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                          />
                                        )}
                                        <Chip
                                          label={service.isActive ? 'Active' : 'Inactive'}
                                          color={service.isActive ? 'success' : 'default'}
                                          size="small"
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <Grid container spacing={0} sx={{ mt: 1 }}>
                                        <Grid xs={6} md={3}>
                                          <Typography variant="body2" color="text.secondary">
                                            Base Price
                                          </Typography>
                                          <Typography variant="body2" fontWeight={500}>
                                            ₹{service.basePrice}
                                          </Typography>
                                        </Grid>
                                        <Grid xs={6} md={3}>
                                          <Typography variant="body2" color="text.secondary">
                                            Per Unit
                                          </Typography>
                                          <Typography variant="body2" fontWeight={500}>
                                            ₹{service.pricePerUnit || 0} / {service.unit || 'N/A'}
                                          </Typography>
                                        </Grid>
                                        <Grid xs={6} md={3}>
                                          <Typography variant="body2" color="text.secondary">
                                            Express Rate
                                          </Typography>
                                          <Typography variant="body2" fontWeight={500}>
                                            {service.urgencyMultipliers.express}x
                                            {service.urgencyTimes?.express && 
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                {service.urgencyTimes.express}
                                              </Typography>
                                            }
                                          </Typography>
                                        </Grid>
                                        <Grid xs={6} md={3}>
                                          <Typography variant="body2" color="text.secondary">
                                            Urgent Rate
                                          </Typography>
                                          <Typography variant="body2" fontWeight={500}>
                                            {service.urgencyMultipliers.urgent}x
                                            {service.urgencyTimes?.urgent && 
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                {service.urgencyTimes.urgent}
                                              </Typography>
                                            }
                                          </Typography>
                                        </Grid>
                                        {service.maxWeight && (
                                          <Grid xs={6} md={3}>
                                            <Typography variant="body2" color="text.secondary">
                                              Max Weight
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {service.maxWeight} kg
                                            </Typography>
                                          </Grid>
                                        )}
                                        {service.durationInMinutes && (
                                          <Grid xs={6} md={3}>
                                            <Typography variant="body2" color="text.secondary">
                                              Duration
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                              {formatDurationForAPI(service.durationInMinutes)}
                                            </Typography>
                                          </Grid>
                                        )}
                                      </Grid>
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditRate(service)}
                                      sx={{ mr: 1 }}
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteRate(service.id)}
                                      color="error"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                              </List>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        ))}
      </Paper>
    );
  };

  return (
    <Box>
      <PageHeader title="Services & Pricing" subtitle="Segments, categories and rates customers see when booking" />

      {/* Duplicate Detection Summary */}
      {(segmentDuplicates.totalDuplicates > 0 || categoryDuplicates.totalDuplicates > 0) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {segmentDuplicates.totalDuplicates > 0 && (
            <Grid xs={12} md={6}>
              <Alert severity="warning" sx={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Duplicate Segments Detected
                  </Typography>
                  <Typography variant="body2">
                    {segmentDuplicates.totalDuplicates} duplicate segments found across {segmentDuplicates.duplicates.length} groups
                  </Typography>
                </Box>
              </Alert>
            </Grid>
          )}
          {categoryDuplicates.totalDuplicates > 0 && (
            <Grid xs={12} md={6}>
              <Alert severity="warning" sx={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Duplicate Categories Detected
                  </Typography>
                  <Typography variant="body2">
                    {categoryDuplicates.totalDuplicates} duplicate categories found across {categoryDuplicates.duplicates.length} groups
                  </Typography>
                </Box>
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
          <Tab label="Service Segments" />
          <Tab label="Service Categories" />
          <Tab label="Pricing & Rates" />
          <Tab label="Payment Settings" />
        </Tabs>
      </Box>

      {/* Service Segments Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Service Segments</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortConfig.segments.field}
                label="Sort by"
                onChange={(e) => handleSort('segments', e.target.value)}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="order">Order</MenuItem>
                <MenuItem value="createdAt">Created Date</MenuItem>
                <MenuItem value="isActive">Status</MenuItem>
              </Select>
            </FormControl>
            <IconButton 
              onClick={() => handleSort('segments', sortConfig.segments.field)}
              title={`Sort ${sortConfig.segments.direction === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortConfig.segments.direction === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
            </IconButton>
            <Button startIcon={<Add />} onClick={handleAddSegment} variant="contained">
              Add Segment
            </Button>
          </Box>
        </Box>

        <ResponsiveTable
          columns={[
            {
              key: 'name',
              header: (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => handleSort('segments', 'name')}
                >
                  Segment {getSortIcon('segments', 'name')}
                </Box>
              ),
              mobile: 'title',
              render: (segment: any) => {
                const duplicateInfo = isSegmentDuplicate(segment.name, segment.id);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getIconComponent(segment.icon)}
                    <Typography sx={{ ml: 2 }} variant="subtitle2" fontWeight={600}>
                      {segment.name}
                    </Typography>
                    {duplicateInfo.isDuplicate && (
                      <Chip
                        label={`${duplicateInfo.count} duplicates`}
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                        icon={<Warning />}
                      />
                    )}
                  </Box>
                );
              },
            },
            {
              key: 'description',
              header: 'Description',
              mobile: 'subtitle',
              render: (segment: any) => segment.description,
            },
            {
              key: 'color',
              header: 'Colour',
              render: (segment: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: segment.color,
                      borderRadius: '50%',
                      mr: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                  <Typography variant="body2">{segment.color}</Typography>
                </Box>
              ),
            },
            {
              key: 'categories',
              header: 'Categories',
              render: (segment: any) =>
                `${categories.filter((c) => c.segmentId === segment.id).length} categories`,
            },
            {
              key: 'order',
              header: (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => handleSort('segments', 'order')}
                >
                  Order {getSortIcon('segments', 'order')}
                </Box>
              ),
              render: (segment: any) => segment.order,
            },
            {
              key: 'status',
              header: (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => handleSort('segments', 'isActive')}
                >
                  Status {getSortIcon('segments', 'isActive')}
                </Box>
              ),
              render: (segment: any) => (
                <StatusChip status={segment.isActive ? 'active' : 'inactive'} />
              ),
            },
            {
              key: 'actions',
              header: 'Actions',
              align: 'right',
              mobile: 'actions',
              render: (segment: any) => (
                <>
                  <IconButton
                    size="small"
                    onClick={() => handleEditSegment(segment)}
                    aria-label="Edit segment"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSegment(segment.id)}
                    aria-label="Delete segment"
                  >
                    <Delete />
                  </IconButton>
                </>
              ),
            },
          ]}
          rows={sortData(segments, sortConfig.segments.field, sortConfig.segments.direction)}
          rowKey={(segment) => segment.id}
          rowSx={(segment) =>
            isSegmentDuplicate(segment.name, segment.id).isDuplicate
              ? { backgroundColor: 'warning.light' }
              : {}
          }
          aria-label="Service segments"
          emptyTitle="No service segments"
          emptyDescription="Segments group the categories customers pick from. Add one to get started."
        />
      </TabPanel>

      {/* Service Categories Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Service Categories</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortConfig.categories.field}
                label="Sort by"
                onChange={(e) => handleSort('categories', e.target.value)}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="createdAt">Created Date</MenuItem>
                <MenuItem value="isActive">Status</MenuItem>
              </Select>
            </FormControl>
            <IconButton 
              onClick={() => handleSort('categories', sortConfig.categories.field)}
              title={`Sort ${sortConfig.categories.direction === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortConfig.categories.direction === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
            </IconButton>
            <Button startIcon={<Add />} onClick={handleAddCategory} variant="contained">
              Add Category
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('categories', 'name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Category {getSortIcon('categories', 'name') && <span>{getSortIcon('categories', 'name')}</span>}
                  </Box>
                </TableCell>
                <TableCell>Segment</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Services</TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('categories', 'isActive')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Status {getSortIcon('categories', 'isActive') && <span>{getSortIcon('categories', 'isActive')}</span>}
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer' }} 
                  onClick={() => handleSort('categories', 'createdAt')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Created {getSortIcon('categories', 'createdAt') && <span>{getSortIcon('categories', 'createdAt')}</span>}
                  </Box>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortData(categories, sortConfig.categories.field, sortConfig.categories.direction).map((category) => {
                const segment = segments.find(s => s.id === category.segmentId);
                const duplicateInfo = isCategoryDuplicate(category.name, category.id, category.segmentId);
                return (
                  <TableRow key={category.id} sx={duplicateInfo.isDuplicate ? { backgroundColor: 'warning.light', '&:hover': { backgroundColor: 'warning.main' } } : {}}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getIconComponent(category.icon)}
                        <Typography sx={{ ml: 2 }} variant="subtitle2" fontWeight={600}>
                          {category.name}
                        </Typography>
                        {duplicateInfo.isDuplicate && (
                          <Chip
                            label={`${duplicateInfo.count} duplicates`}
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                            icon={<Warning />}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: segment?.color || '#ccc',
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2">{segment?.name || 'Unknown'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{category.description}</TableCell>
                    <TableCell>
                      {serviceRates.filter(r => r.categoryId === category.id).length} services
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={category.isActive ? 'Active' : 'Inactive'}
                        color={category.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{category.createdAt.toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEditCategory(category)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteCategory(category.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Pricing & Rates Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Service Rates</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort services by</InputLabel>
              <Select
                value={sortConfig.serviceRates.field}
                label="Sort services by"
                onChange={(e) => handleSort('serviceRates', e.target.value)}
              >
                <MenuItem value="serviceName">Service Name</MenuItem>
                <MenuItem value="basePrice">Base Price (Low to High)</MenuItem>
                <MenuItem value="pricePerUnit">Price per Unit</MenuItem>
                <MenuItem value="maxWeight">Max Weight (Kg)</MenuItem>
                <MenuItem value="durationInMinutes">Duration</MenuItem>
                <MenuItem value="urgencyMultipliers.express">Express Rate</MenuItem>
                <MenuItem value="urgencyMultipliers.urgent">Urgent Rate</MenuItem>
                <MenuItem value="createdAt">Created Date</MenuItem>
                <MenuItem value="isActive">Status</MenuItem>
              </Select>
            </FormControl>
            <IconButton 
              onClick={() => handleSort('serviceRates', sortConfig.serviceRates.field)}
              title={`Sort ${sortConfig.serviceRates.direction === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortConfig.serviceRates.direction === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
            </IconButton>
            <Button startIcon={<Add />} onClick={handleAddRate} variant="contained">
              Add Rate
            </Button>
          </Box>
        </Box>

        {renderTreeViewServiceRates()}
      </TabPanel>

      {/* Payment Settings Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Payment sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Payment Gateways
                  </Typography>
                </Box>
                <List>
                  {paymentGateways.map((gateway) => (
                    <ListItem key={gateway.id}>
                      <ListItemText
                        primary={gateway.name}
                        secondary={`Processing Fee: ${gateway.processingFee}%`}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={gateway.isEnabled}
                          onChange={(e) => handleToggleGateway(gateway.id, e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Payment Options
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Cash on Delivery"
                      secondary="Allow cash payments on delivery"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={paymentSettings.codEnabled}
                        onChange={(e) => setPaymentSettings({
                          ...paymentSettings,
                          codEnabled: e.target.checked
                        })}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
                <Divider sx={{ my: 2 }} />
                <TextField
                  fullWidth
                  label="Minimum Order Amount"
                  type="text"
                  value={paymentSettings.minimumOrderAmount}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    minimumOrderAmount: Math.floor(handleNumberInput(e.target.value, 0))
                  })}
                  onKeyPress={handleNumberKeyPress}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Maximum COD Amount"
                  type="text"
                  value={paymentSettings.maxCodAmount}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    maxCodAmount: Math.floor(handleNumberInput(e.target.value, 0))
                  })}
                  onKeyPress={handleNumberKeyPress}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Segment Dialog */}
      <Dialog open={segmentDialogOpen} onClose={() => setSegmentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSegment?.id ? 'Edit Service Segment' : 'Create New Service Segment'}
        </DialogTitle>
        <DialogContent>
          {editingSegment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                fullWidth
                label="Segment Name"
                value={editingSegment.name}
                onChange={(e) => setEditingSegment({ ...editingSegment, name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editingSegment.description}
                onChange={(e) => setEditingSegment({ ...editingSegment, description: e.target.value })}
                required
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <IconPicker
                    selectedIcon={editingSegment.icon}
                    onIconSelect={(iconName) => setEditingSegment({ ...editingSegment, icon: iconName })}
                    label="Select Icon"
                  />
                </Box>
                <TextField
                  sx={{ flex: 1 }}
                  label="Color"
                  value={editingSegment.color}
                  onChange={(e) => setEditingSegment({ ...editingSegment, color: e.target.value })}
                  type="color"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="Display Order"
                  value={editingSegment.order || ''}
                  onChange={(e) => setEditingSegment({ ...editingSegment, order: Math.floor(handleNumberInput(e.target.value, 1)) })}
                  onKeyPress={handleNumberKeyPress}
                  helperText="Lower numbers appear first"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingSegment.isActive}
                      onChange={(e) => setEditingSegment({ ...editingSegment, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSegmentDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveSegment} 
            variant="contained"
            disabled={loading.segment}
            startIcon={loading.segment ? <CircularProgress size={20} /> : null}
          >
            {editingSegment?.id ? 'Update' : 'Create'} Segment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCategory?.id ? 'Edit Service Category' : 'Create New Service Category'}
        </DialogTitle>
        <DialogContent>
          {editingCategory && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Service Segment</InputLabel>
                <Select
                  value={editingCategory.segmentId}
                  label="Service Segment"
                  onChange={(e) => setEditingCategory({ ...editingCategory, segmentId: e.target.value })}
                  required
                >
                  {segments.map((segment) => (
                    <MenuItem key={segment.id} value={segment.id}>
                      {segment.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Category Name"
                value={editingCategory.name}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editingCategory.description}
                onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                required
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <IconPicker
                    selectedIcon={editingCategory.icon}
                    onIconSelect={(iconName) => setEditingCategory({ ...editingCategory, icon: iconName })}
                    label="Select Icon"
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingCategory.isActive}
                      onChange={(e) => setEditingCategory({ ...editingCategory, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveCategory} 
            variant="contained"
            disabled={loading.category}
            startIcon={loading.category ? <CircularProgress size={20} /> : null}
          >
            {editingCategory?.id ? 'Update' : 'Create'} Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate Dialog */}
      <Dialog open={rateDialogOpen} onClose={() => setRateDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingRate?.id ? 'Edit Service Rate' : 'Create New Service Rate'}
        </DialogTitle>
        <DialogContent>
          {editingRate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                fullWidth
                label="Service Name"
                value={editingRate.serviceName}
                onChange={(e) => setEditingRate({ ...editingRate, serviceName: e.target.value })}
                required
              />
              <FormControl fullWidth>
                <InputLabel>Service Category</InputLabel>
                <Select
                  value={editingRate.categoryId}
                  label="Service Category"
                  onChange={(e) => setEditingRate({ ...editingRate, categoryId: e.target.value })}
                  required
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <ServiceImageField
                value={editingRate.imageUrl}
                sourceUrl={editingRate.imageSourceUrl}
                // A newly typed link is no longer our stored copy of the old one.
                onChange={(imageUrl) => setEditingRate({ ...editingRate, imageUrl, imageSourceUrl: undefined })}
                onStatusChange={setRateImageState}
              />
              
              {/* Task Type Field */}
              <TextField
                fullWidth
                label="Task Type"
                value={editingRate.taskType || ''}
                onChange={(e) => setEditingRate({ ...editingRate, taskType: e.target.value as any })}
                placeholder="delivery or service"
                helperText="Type 'delivery' for delivery tasks or 'service' for service tasks. This links the rate to the appropriate screen."
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Base Price (₹)"
                  value={editingRate.basePrice || ''}
                  onChange={(e) => setEditingRate({ ...editingRate, basePrice: handleNumberInput(e.target.value, 0) })}
                  onKeyPress={handleNumberKeyPress}
                  helperText="For delivery services - multiplies with pickup/drop points"
                  required
                />
                <TextField
                  fullWidth
                  label="Price Per Unit (₹)"
                  value={editingRate.pricePerUnit || ''}
                  onChange={(e) => setEditingRate({ ...editingRate, pricePerUnit: handleNumberInput(e.target.value, 0) })}
                  onKeyPress={handleNumberKeyPress}
                  helperText="Additional charges per unit"
                  required
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Unit Type</InputLabel>
                  <Select
                    value={editingRate.unit || 'hour'}
                    label="Unit Type"
                    onChange={(e) => setEditingRate({ ...editingRate, unit: e.target.value })}
                  >
                    <MenuItem value="hour">Hour</MenuItem>
                    <MenuItem value="km">Kilometer</MenuItem>
                    <MenuItem value="piece">Piece</MenuItem>
                    <MenuItem value="service">Service</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Max Weight (kg)"
                  value={editingRate.maxWeight || ''}
                  onChange={(e) => setEditingRate({ ...editingRate, maxWeight: handleNumberInput(e.target.value, 0) })}
                  onKeyPress={handleNumberKeyPress}
                  helperText="Maximum weight capacity"
                />
              </Box>
              
              {/* Duration Section */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Service Duration
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Hours"
                  type="number"
                  value={durationHours}
                  onChange={(e) => {
                    const hours = handleNumberInput(e.target.value, 0, 23);
                    setDurationHours(hours);
                  }}
                  inputProps={{ min: 0, max: 23, step: 1 }}
                  helperText="Hours (0-23)"
                />
                <TextField
                  fullWidth
                  label="Minutes"
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => {
                    const minutes = handleNumberInput(e.target.value, 0, 59);
                    setDurationMinutes(minutes);
                  }}
                  inputProps={{ min: 0, max: 59, step: 1 }}
                  helperText="Minutes (0-59)"
                />
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 150 }}>
                  <Typography variant="body2" color="text.secondary">
                    {durationHours === 0 && durationMinutes === 0 
                      ? 'No duration set' 
                      : convertFromMinutes(convertToMinutes(durationHours, durationMinutes)).formatted
                    }
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Urgency Multipliers
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Standard Multiplier"
                  type="number"
                  value={editingRate.urgencyMultipliers.standard || ''}
                  onChange={(e) => setEditingRate({
                    ...editingRate,
                    urgencyMultipliers: {
                      ...editingRate.urgencyMultipliers,
                      standard: handleNumberInput(e.target.value, 0)
                    }
                  })}
                  onKeyPress={handleNumberKeyPress}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="e.g., 1.0 for no change"
                />
                <TextField
                  fullWidth
                  label="Express Multiplier"
                  type="number"
                  value={editingRate.urgencyMultipliers.express || ''}
                  onChange={(e) => setEditingRate({
                    ...editingRate,
                    urgencyMultipliers: {
                      ...editingRate.urgencyMultipliers,
                      express: handleNumberInput(e.target.value, 0)
                    }
                  })}
                  onKeyPress={handleNumberKeyPress}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="e.g., 1.5 for 50% extra"
                />
                <TextField
                  fullWidth
                  label="Urgent Multiplier"
                  type="number"
                  value={editingRate.urgencyMultipliers.urgent || ''}
                  onChange={(e) => setEditingRate({
                    ...editingRate,
                    urgencyMultipliers: {
                      ...editingRate.urgencyMultipliers,
                      urgent: handleNumberInput(e.target.value, 0)
                    }
                  })}
                  onKeyPress={handleNumberKeyPress}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="e.g., 2.0 for double price"
                />
              </Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Urgency Time Constraints
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Standard Time"
                  placeholder="e.g., 24 hrs or 1440 mins"
                  value={editingRate.urgencyTimes?.standard || ''}
                  onChange={(e) => setEditingRate({
                    ...editingRate,
                    urgencyTimes: {
                      ...editingRate.urgencyTimes,
                      standard: e.target.value
                    }
                  })}
                  helperText="Enter duration (e.g., 30 mins, 2 hrs, 1.5 hrs)"
                />
                <TextField
                  fullWidth
                  label="Express Time"
                  placeholder="e.g., 6 hrs or 360 mins"
                  value={editingRate.urgencyTimes?.express || ''}
                  onChange={(e) => setEditingRate({
                    ...editingRate,
                    urgencyTimes: {
                      ...editingRate.urgencyTimes,
                      express: e.target.value
                    }
                  })}
                  helperText="Enter duration (e.g., 30 mins, 2 hrs, 1.5 hrs)"
                />
                <TextField
                  fullWidth
                  label="Urgent Time"
                  placeholder="e.g., 2 hrs or 120 mins"
                  value={editingRate.urgencyTimes?.urgent || ''}
                  onChange={(e) => setEditingRate({
                    ...editingRate,
                    urgencyTimes: {
                      ...editingRate.urgencyTimes,
                      urgent: e.target.value
                    }
                  })}
                  helperText="Enter duration (e.g., 30 mins, 2 hrs, 1.5 hrs)"
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingRate.isActive}
                    onChange={(e) => setEditingRate({ ...editingRate, isActive: e.target.checked })}
                  />
                }
                label="Active Service Rate"
                sx={{ mt: 2 }}
              />
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!editingRate.subscriptionEnabled}
                      onChange={(e) => setEditingRate({ ...editingRate, subscriptionEnabled: e.target.checked })}
                    />
                  }
                  label="Available as a subscription"
                />
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: editingRate.subscriptionEnabled ? 2 : 0 }}>
                  Customers can book recurring visits — daily, alternate days or chosen weekdays — and skip or pause days.
                  Individual partners and agencies in this service can take them on.
                </Typography>
                {editingRate.subscriptionEnabled && (
                  <TextField
                    fullWidth
                    label="Subscription price per visit (₹)"
                    value={editingRate.subscriptionPrice || ''}
                    onChange={(e) => setEditingRate({ ...editingRate, subscriptionPrice: handleNumberInput(e.target.value, 0) || undefined })}
                    onKeyPress={handleNumberKeyPress}
                    helperText={`Leave empty to charge the base price (₹${editingRate.basePrice || 0}) per visit. A city price override still wins.`}
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveRate} 
            variant="contained"
            disabled={loading.rate}
            startIcon={loading.rate ? <CircularProgress size={20} /> : null}
          >
            {editingRate?.id ? 'Update' : 'Create'} Service Rate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, type: 'segment', id: '', name: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Delete {deleteConfirm.type === 'segment' ? 'Segment' : deleteConfirm.type === 'category' ? 'Category' : 'Service Rate'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? 
            {deleteConfirm.type === 'segment' && ' This will also delete all associated categories and service rates.'}
            {deleteConfirm.type === 'category' && ' This will also delete all associated service rates.'}
            <br /><br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, type: 'segment', id: '', name: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (deleteConfirm.type === 'segment') confirmDeleteSegment();
              else if (deleteConfirm.type === 'category') confirmDeleteCategory();
              else confirmDeleteRate();
            }} 
            color="error" 
            variant="contained"
            disabled={loading.delete}
            startIcon={loading.delete ? <CircularProgress size={20} color="inherit" /> : <Delete />}
          >
            {loading.delete ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServicesPricing;
